import { Field, ImageField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Link, { LinkVariant } from 'components/helpers/Link/Link';
import ResponsiveImages from 'components/helpers/ResponsiveImages/ResponsiveImages';
import { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import styles from './EnrollmentKitDownloadBanner.module.scss';
import { useEffect, useState } from 'react';

const cx = classNames.bind(styles);

type EnrollmentKitDownloadBannerProps = ComponentProps & {
  fields: {
    image: ImageField;
    medicareBody: Field<string>;
    snpBody: Field<string>;
    fallbackBody: Field<string>;
    downloadLinkText: Field<string>;
    title: Field<string>;
    linkLookupSnp: string[];
    linkLookupMedicare: string[];
    snpKitsByCounty: Record<string, number>;
    medicareKitsByCounty: Record<string, number>;
  };
};

const EnrollmentKitDownloadBanner = (props: EnrollmentKitDownloadBannerProps): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { className, fields } = props;
  const [formData, setFormData] = useState<Record<string, string>>({});

  useEffect(() => {
    try {
      const rawData = sessionStorage.getItem('postSubmitFormData');

      if (rawData) {
        const parsedData: Record<string, string> = JSON.parse(rawData);
        setFormData(parsedData);
        sessionStorage.removeItem('postSubmitFormData');
      }
    } catch (error) {
      console.error('Failed to parse enrollmentKitFormData from sessionStorage:', error);
    }
  }, []);

  let destUrl = '';
  let body = fields.fallbackBody;
  const stateAbbrev = formData?.['formdata-state'] ?? 'NA';
  const key = formData['formdata-county'] + '-' + stateAbbrev;

  if (formData?.['formdata-issnp'] == '1' && key in fields.snpKitsByCounty) {
    body = fields.snpBody;
    destUrl = fields.linkLookupSnp[fields.snpKitsByCounty[key]];
  } else if (formData?.['formdata-issnp'] == '0' && key in fields.medicareKitsByCounty) {
    body = fields.medicareBody;
    destUrl = fields.linkLookupMedicare[fields.medicareKitsByCounty[key]];
  }

  const contentBackgroundColor = 'bg-purple-60 text-white bg-inherit-dark';
  const presentationMode = '50-50';
  const linkIconValue = 'download';
  const linkModeValue = 'tertiary' as LinkVariant;
  const bannerTitleHeadingLevelValue = 'h1';
  const bannerTitleTextStyleValue = 'font-style-xl';
  const imagecolumnClasses: string = cx('image-column');
  const textcolumnClasses: string = cx('text-column', contentBackgroundColor);

  const imageColumn = (
    <div className={imagecolumnClasses} key="standard-page-banner-image-column">
      <ResponsiveImages
        desktopImage={fields.image}
        mobileImage={fields.image}
        tabletImage={fields.image}
        imageType={{
          name: 'Cover',
          fields: { Value: { value: 'image-type--cover' } },
        }}
        imageAreaBackgroundColor={null}
      />
    </div>
  );
  const hasWhiteText = contentBackgroundColor.includes('text-white');
  const textColumn = (
    <div className={textcolumnClasses} key="standard-page-banner-text-column">
      <Text
        className={cx({ title: true, 'text-white': hasWhiteText, [bannerTitleTextStyleValue]: true })}
        encode={sitecoreContext.pageEditing}
        field={sitecoreContext.pageEditing ? fields.title : { value: fields.title?.value }}
        tag={bannerTitleHeadingLevelValue}
        editable={true}
      />
      <RichTextWrapper
        className={cx('body')}
        field={sitecoreContext.pageEditing ? body : { value: body?.value }}
        tag="div"
      />
      {destUrl && (
        <Link
          className={cx('link', { 'text-white': hasWhiteText })}
          field={{ value: { href: destUrl } }}
          icon={linkIconValue}
          showLinkTextWithChildrenPresent={false}
          variant={linkModeValue}
        >
          <Text
            field={sitecoreContext.pageEditing ? fields.downloadLinkText : { value: fields.downloadLinkText?.value }}
            encode={sitecoreContext.pageEditing}
          />
        </Link>
      )}
    </div>
  );

  return (
    <div className={cx('banner', `mode-${presentationMode}`, 'component-container-edge', className)}>
      {[textColumn, imageColumn]}
    </div>
  );
};

export default compose<EnrollmentKitDownloadBannerProps>(
  withValidDatasourceCheck(),
  withStyles()
)(EnrollmentKitDownloadBanner);
